// Roku External Control Protocol (ECP) helpers — direct LAN calls to the Roku
// on port 8060. Uses cordova.plugin.http to bypass WKWebView cross-origin and
// mixed-content restrictions (native-only).

const ECP_PORT = 8060;

function getHttp() {
  return window.cordova && window.cordova.plugin && window.cordova.plugin.http;
}

function url(ip, path) {
  return `http://${ip}:${ECP_PORT}${path}`;
}

export function ecpGet(ip, path) {
  return new Promise((resolve, reject) => {
    const http = getHttp();
    if (!http) { reject(new Error('cordova.plugin.http unavailable')); return; }
    http.sendRequest(
      url(ip, path),
      { method: 'get', responseType: 'text', connectTimeout: 3, readTimeout: 3 },
      resp => resolve(resp),
      err => reject(err)
    );
  });
}

export function ecpPost(ip, path) {
  return new Promise((resolve, reject) => {
    const http = getHttp();
    if (!http) { reject(new Error('cordova.plugin.http unavailable')); return; }
    http.sendRequest(
      url(ip, path),
      { method: 'post', responseType: 'text', connectTimeout: 3, readTimeout: 3, data: {} },
      resp => resolve(resp),
      err => reject(err)
    );
  });
}

export function sendKey(ip, key) {
  return ecpPost(ip, '/keypress/' + key);
}

export function launchApp(ip, appId) {
  return ecpPost(ip, '/launch/' + appId);
}

// Send a string one character at a time via /keypress/Lit_<char>, 80ms apart.
export async function sendText(ip, text) {
  for (const ch of text) {
    await ecpPost(ip, '/keypress/Lit_' + encodeURIComponent(ch));
    await new Promise(r => setTimeout(r, 80));
  }
}

function parseXml(text) {
  return new DOMParser().parseFromString(text, 'text/xml');
}

function formatTime(ms) {
  const total = Math.floor(ms / 1000);
  const hrs = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  const pad = n => (n < 10 ? '0' + n : '' + n);
  if (hrs > 0) return `${hrs}:${pad(mins)}:${pad(secs)}`;
  return `${mins}:${pad(secs)}`;
}

export async function queryActiveApp(ip) {
  const resp = await ecpGet(ip, '/query/active-app');
  if (resp.status !== 200 || !resp.data) return { activeApp: '' };
  const doc = parseXml(resp.data);
  const appEl = doc.getElementsByTagName('app')[0];
  const name = appEl ? (appEl.textContent || '') : '';
  return { activeApp: name === 'Roku' ? 'Home Screen' : (name || 'Home Screen') };
}

export async function queryMediaPlayer(ip) {
  const resp = await ecpGet(ip, '/query/media-player');
  if (resp.status !== 200 || !resp.data) return { playerState: '' };
  const doc = parseXml(resp.data);
  const player = doc.getElementsByTagName('player')[0];
  if (!player) return { playerState: '' };

  const raw = player.getAttribute('state') || '';
  let label = '';
  if (raw === 'play') label = 'Playing';
  else if (raw === 'pause') label = 'Paused';
  else if (raw === 'stop' || raw === 'close' || raw === 'none') label = 'Idle';
  else if (raw) label = raw.charAt(0).toUpperCase() + raw.slice(1);

  const posEl = doc.getElementsByTagName('position')[0];
  const durEl = doc.getElementsByTagName('duration')[0];
  let position = '';
  let duration = '';
  if (posEl && durEl) {
    const posMs = parseInt(posEl.textContent) || 0;
    const durMs = parseInt(durEl.textContent) || 0;
    if (durMs > 0) {
      position = formatTime(posMs);
      duration = formatTime(durMs);
    }
  }

  const pluginEl = doc.getElementsByTagName('plugin')[0];
  const contentTitle = pluginEl ? (pluginEl.getAttribute('name') || pluginEl.textContent || '') : '';

  const liveEl = doc.getElementsByTagName('is_live')[0];
  const isLive = liveEl && liveEl.textContent === 'true';

  return {
    playerState: isLive ? (label ? label + ' (Live)' : 'Live') : (label || 'Idle'),
    position,
    duration,
    contentTitle
  };
}

export async function queryApps(ip) {
  const resp = await ecpGet(ip, '/query/apps');
  if (resp.status !== 200 || !resp.data) return [];
  const doc = parseXml(resp.data);
  const els = doc.getElementsByTagName('app');
  const apps = [];
  for (let i = 0; i < els.length; i++) {
    apps.push({
      id: els[i].getAttribute('id'),
      name: els[i].textContent || 'Unknown',
      type: els[i].getAttribute('type') || '',
      version: els[i].getAttribute('version') || ''
    });
  }
  apps.sort((a, b) => a.name.localeCompare(b.name));
  return apps;
}

export async function queryDeviceInfo(ip) {
  const resp = await ecpGet(ip, '/query/device-info');
  if (resp.status !== 200 || !resp.data) return {};
  const doc = parseXml(resp.data);
  const tag = t => {
    const el = doc.getElementsByTagName(t)[0];
    return el ? el.textContent : '';
  };
  return {
    name: tag('user-device-name') || tag('friendly-device-name'),
    model: tag('model-name'),
    serial: tag('serial-number'),
    softwareVersion: tag('software-version'),
    powerMode: tag('power-mode'),
    networkType: tag('network-type'),
    wifiMac: tag('wifi-mac')
  };
}

export function isLimitedModeError(err) {
  return err && err.status === 403;
}
